import { HttpErrorResponse } from '@angular/common/http';
import { CurrentContext } from '../../core/context/current-context.model';

const DEFAULT_ERROR_PREFIX = 'shared.aiAssist.errors';

export function extractAiAssistErrorCode(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  const body = error.error as Record<string, unknown> | null;
  if (!body) {
    return null;
  }

  if (typeof body['error'] === 'string') {
    return body['error'];
  }

  if (typeof body['Error'] === 'string') {
    return body['Error'];
  }

  return null;
}

/**
 * Resolves a localized AI assist error message.
 * Looks up `{errorKeyPrefix}.{code}`, then rate-limit (429), then `.generic`.
 */
export function resolveAiAssistError(
  error: unknown,
  translate: (key: string) => string,
  errorKeyPrefix = DEFAULT_ERROR_PREFIX
): string {
  const code = extractAiAssistErrorCode(error);
  if (code) {
    const key = `${errorKeyPrefix}.${code}`;
    const translated = translate(key);
    if (translated !== key) {
      return translated;
    }
  }

  if (error instanceof HttpErrorResponse && error.status === 429) {
    const rateKey = `${errorKeyPrefix}.rate_limit_exceeded`;
    const fallback = translate(rateKey);
    if (fallback !== rateKey) {
      return fallback;
    }
  }

  return translate(`${errorKeyPrefix}.generic`);
}

/**
 * Optional permission helper. Hosts pass their domain permission key
 * (e.g. `growth.content.ai.assist`). SystemAdmin always passes.
 * When `requiredAction` is omitted, any non-empty action list grants access.
 */
export function hasAiAssistPermission(
  context: CurrentContext | null,
  permissionKey: string,
  requiredAction?: string | null
): boolean {
  if (!permissionKey?.trim()) {
    return false;
  }

  const roles = new Set(context?.roles ?? []);
  if (roles.has('SystemAdmin')) {
    return true;
  }

  const node = (context?.permissions ?? []).find(item => item.key === permissionKey);
  if (!node) {
    return false;
  }

  const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
  if (!requiredAction?.trim()) {
    return actions.size > 0;
  }

  return actions.has(requiredAction.trim().toLowerCase());
}

export function truncateAiAssistPreview(value: string | null | undefined, maxLength = 160): string {
  const text = (value ?? '').replace(/\s+/g, ' ').trim();
  if (!text) {
    return '—';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1)}…`;
}
